<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          Knowledge Bases
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div
              class="card-header d-flex justify-content-between align-items-center"
            >
              <div>
                <h5 class="mb-1">Knowledge Bases</h5>
                <small class="text-muted">
                  Manage your knowledge bases and document collections
                </small>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="loadKnowledgeBases"
                  :disabled="isLoading"
                >
                  <i class="fa fa-refresh me-1"></i>
                  Refresh
                </button>
                <button
                  @click="router.push('/admin/knowledge-bases/new')"
                  class="btn btn-primary btn-sm"
                  :disabled="!canCreate('knowledge bases')"
                >
                  <i class="fa fa-plus me-1"></i>
                  Create Knowledge Base
                </button>
              </div>
            </div>

            <div class="card-body"> 
              <!-- Knowledge Bases Table -->
              <div  
                class="table-responsive theme-scrollbar"
              >
                <table class="table table-hover align-middle" style="min-width: 1400px;">
                  <thead class="table-dark">
                    <tr>
                      <th style="min-width: 200px;">Name</th>
                      <th style="min-width: 200px;">Description</th>
                      <th style="width: 150px;">Category</th>
                      <th style="width: 130px;">Documents</th>
                      <th style="width: 200px;">Source Types</th>
                      <th style="width: 200px;">Status</th>
                      <th style="width: 200px;">Created</th>
                      <th style="width: 200px;">Actions</th>
                    </tr>
                  </thead>
                  <!-- Loading State -->
                  <tbody v-if="isLoading">
                   <tr>
                      <td
                      :colspan="8"
                      class="text-center py-4"
                     >
                    <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                   </div>
                       </td>
                   </tr>
                 </tbody>
                   <!-- Empty State -->
                  <tbody v-else-if="knowledgeBases.length < 1">
                   <tr>
                      <td
                      :colspan="8"
                      class="text-center py-4"
                     >
                     <div class="text-center py-5">
                <div class="mb-3">
                  <i class="fa fa-database fa-3x text-muted"></i>
                </div>
                <h5 class="text-muted">No Knowledge Bases Found</h5>
                <p class="text-muted">
                  Create your first knowledge base to get started.
                </p>
                <div class="mt-3">
                  <button
                    @click="router.push('/admin/knowledge-bases/new')"
                    class="btn btn-primary"
                    :disabled="!canCreate('knowledge bases')"
                  >
                    <i class="fa fa-plus me-1"></i>
                    Create Knowledge Base
                  </button>
                </div>
              </div>
                       </td>
                   </tr>
                 </tbody>
                  <tbody v-else-if="knowledgeBases.length > 0">
                    <tr v-for="kb in knowledgeBases" :key="kb._id">
                      <td>
                        <strong>{{ kb.name }}</strong>
                      </td>
                      <td>
                        <span class="text-muted small">
                          {{ kb.description || "No description" }}
                        </span>
                      </td>
                      <td>
                        <span v-if="kb.category" class="badge bg-secondary text-capitalize">
                          {{ kb.category?.name }}
                        </span>
                        <span v-else class="text-muted small">-</span>
                      </td>
                      <td>
                        <span class="badge bg-info">
                          {{ kb.document_count || 0 }}
                        </span>
                      </td>
                      <td>
                        <div class="d-flex gap-1 flex-wrap">
                         <span
                          v-if="kb && kb.source_type && kb.source_type.trim()"
                        class="badge bg-primary"
                        :title="kb.source_type"
                       >
  
                        {{ kb.source_type }}
                        </span>
                          <!-- <span
                            v-if="kb.sourceTypes?.file"
                            class="badge bg-primary"
                            :title="`${kb.sourceTypes.file} files`"
                          >
                            <i class="fa fa-file me-1"></i>
                            {{ kb.sourceTypes.file }}
                          </span>
                          <span
                            v-if="kb.sourceTypes?.url"
                            class="badge bg-success"
                            :title="`${kb.sourceTypes.url} URLs`"
                          >
                            <i class="fa fa-link me-1"></i>
                            {{ kb.sourceTypes.url }}
                          </span>
                          <span
                            v-if="kb.sourceTypes?.text"
                            class="badge bg-warning"
                            :title="`${kb.sourceTypes.text} text documents`"
                          >
                            <i class="fa fa-align-left me-1"></i>
                            {{ kb.sourceTypes.text }}
                          </span> -->
                        </div>
                      </td>
                      <td>
                        <span
                          :class="
                            kb.is_active
                              ? 'badge bg-success'
                              : 'badge bg-secondary'
                          "
                        >
                          {{ kb.is_active ? "Active" : "Inactive" }}
                        </span>
                      </td>
                      <td>
                        <small class="text-muted">
                          {{ formatDate(kb.created_at) }}
                        </small>
                      </td>
                      <td>
                        <div class="d-flex gap-1 justify-content-end">
                          <button
                            @click="router.push(`/admin/knowledge-bases/${kb._id}`)"
                            class="btn btn-sm btn-outline-primary"
                            title="View Details"
                            :disabled="!canView('knowledge bases')"
                          >
                            <i class="fa fa-eye"></i>
                          </button>
                          <button
                            @click="router.push(`/admin/knowledge-bases/${kb._id}/edit`)"
                            class="btn btn-sm btn-outline-secondary"
                            title="Edit"
                            :disabled="!canUpdate('knowledge bases')"
                          >
                            <i class="fa fa-edit"></i>
                          </button>
                          <button
                            @click="toggleStatus(kb)"
                            class="btn btn-sm btn-outline-warning"
                            :title="kb.is_active ? 'Deactivate' : 'Activate'"
                            :disabled="!canUpdate('knowledge bases')"
                          >
                            <i
                              :class="
                                kb.is_active ? 'fa fa-pause' : 'fa fa-play'
                              "
                            ></i>
                          </button>
                          <button
                            @click="confirmDelete(kb)"
                            class="btn btn-sm btn-outline-danger"
                            title="Delete"
                            :disabled="!canDelete('knowledge bases')"
                          >
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button
              type="button"
              class="btn-close"
              @click="showDeleteModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <p>
              Are you sure you want to delete the knowledge base
              <strong>{{ selectedKB?.name }}</strong
              >?
            </p>
            <p class="text-danger small mb-0">
              <i class="fa fa-exclamation-triangle me-1"></i>
              This action cannot be undone. All documents will be removed.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showDeleteModal = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteKnowledgeBase"
              :disabled="isDeleting"
            >
              <span v-if="isDeleting">
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                Deleting...
              </span>
              <span v-else>
                <i class="fa fa-trash me-1"></i>
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

// Types
interface KnowledgeBase {
  _id: string;
  name: string;
  description?: string;
  is_active: boolean;
  document_count?: number;
  sourceTypes?: {
    file?: number;
    url?: number;
    text?: number;
  };
  source_type?: string,
  category?: {_id?: string, name?: string
},
  created_at: string;
  updatedAt: string;
}

// Reactive data
const knowledgeBases = ref<KnowledgeBase[]>([]);
const isLoading = ref(false);
const isDeleting = ref(false);
const error = ref<string | null>(null);
const showDeleteModal = ref(false);
const selectedKB = ref<KnowledgeBase | null>(null);
const router = useRouter();

// Methods
const loadKnowledgeBases = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await apiService.getKnowledgeBases();

    if (Array.isArray(response)) {
      knowledgeBases.value = response;
    } else if (response.data && Array.isArray(response.data)) {
      knowledgeBases.value = response.data;
    } else {
      knowledgeBases.value = [];
    }
  } catch (err: any) {
    console.error("Error loading knowledge bases:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load knowledge bases";
    showToast("Error loading knowledge bases", "error");
  } finally {
    isLoading.value = false;
  }
};

const toggleStatus = async (kb: KnowledgeBase) => {
   const payload = { 
      is_active: !kb.is_active,
    };
  try {
    await apiService.updateKnowledgeBase(kb._id, payload);
    kb.is_active = !kb.is_active;
    showToast(
      `Knowledge base ${
        kb.is_active ? "activated" : "deactivated"
      } successfully`,
      "success"
    );
  } catch (err: any) {
    console.error("Error toggling status:", err);
    showToast(
      err?.response?.data?.message || "Failed to update status",
      "error"
    );
  }
};

const confirmDelete = (kb: KnowledgeBase) => {
  selectedKB.value = kb;
  showDeleteModal.value = true;
};

const deleteKnowledgeBase = async () => {
  if (!selectedKB.value) return;

  isDeleting.value = true;
  try {
    await apiService.deleteKnowledgeBase(selectedKB.value._id);
    knowledgeBases.value = knowledgeBases.value.filter(
      (kb) => kb._id !== selectedKB.value?._id
    );
    showDeleteModal.value = false;
    showToast("Knowledge base deleted successfully", "success");
  } catch (err: any) {
    console.error("Error deleting knowledge base:", err);
    showToast(
      err?.response?.data?.message || "Failed to delete knowledge base",
      "error"
    );
  } finally {
    isDeleting.value = false;
    selectedKB.value = null;
  }
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC", // ensures no date shift from Z timezone
  }).format(date);
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  // Simple toast implementation - you can replace with your toast library
  console.log(`[${type.toUpperCase()}] ${message}`);

  // You can integrate with a toast library like vue-toastification here
  // For now, using alert as fallback
  if (type === "error") {
    alert(message);
  }
};

// Lifecycle
onMounted(() => {
  loadKnowledgeBases();
});
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}
 

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
}

.modal.show {
  display: block;
}
</style>
